import {callRemote, DELETE, GET, POST} from "./RemoteService";

// Editors' finished photos go straight to ECT's S3 bucket. The creators API asks ECT for presigned
// PUT URLs (it holds ECT's token), and the browser PUTs each file to its URL - the bytes never pass
// through either backend. Submitting the job afterwards is what calls ECT's photos-complete.
//
// The gallery is built over as many sessions as the editor needs, and S3 is its only record: what
// listPhotos returns is the gallery. Removing a photo deletes it from S3.

// The single images ECT accepts. Every one is part of the deliverable, so all are required.
export const SINGLE_IMAGES = [
    {kind: 'passenger-white', label: 'Thumbnail (passenger side, white background)'},
    {kind: 'passenger-transparent', label: 'Passenger side, transparent background'},
    {kind: 'front-quarter-driver', label: 'Front quarter, driver side (lifestyle)'},
    {kind: 'banner', label: 'Banner (for advertising)'},
];

const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/avif'];
const TYPE_BY_EXTENSION = {jpg: 'image/jpeg', jpeg: 'image/jpeg', png: 'image/png', webp: 'image/webp', avif: 'image/avif'};

// ECT allows 300 gallery files per request; galleries run 21-210, so this is normally one request.
const MAX_GALLERY_PER_REQUEST = 300;
const PARALLEL_UPLOADS = 6;
const ATTEMPTS = 3;

export const contentTypeOf = (file) => {
    if (ALLOWED_TYPES.includes(file.type)) {
        return file.type;
    }
    // Some browsers leave file.type blank for avif/webp.
    const extension = file.name.split('.').pop().toLowerCase();
    return TYPE_BY_EXTENSION[extension] || file.type || 'unknown';
};

// Mirrors ECT's rules, so a bad file is caught before anything is uploaded. ECT rejects rather than
// renames a bad filename.
export const galleryFileProblem = (file) => {
    const name = file.name.trim();
    if (!name || name.includes('/') || name.includes('\\') || name.includes('..') || name.startsWith('.')) {
        return `"${file.name}" is not a usable filename`;
    }
    if (!ALLOWED_TYPES.includes(contentTypeOf(file))) {
        return `"${file.name}" must be a JPEG, PNG, WebP or AVIF image`;
    }
    return null;
};

/** What is in S3 for the job now: {images: [...], gallery: [...]}, the gallery in display order. */
export const listPhotos = (getAccessTokenSilently, jobId) =>
    callRemote(getAccessTokenSilently, GET, `/jobs/${jobId}/photos`);

export const removeGalleryPhoto = (getAccessTokenSilently, jobId, name) =>
    callRemote(getAccessTokenSilently, DELETE, `/jobs/${jobId}/photos/gallery/${encodeURIComponent(name)}`);

/**
 * Removes every gallery photo for the job - from S3, at once, with no undo. The single images stay.
 * Resolves to {removed: n}.
 */
export const clearGallery = (getAccessTokenSilently, jobId) =>
    callRemote(getAccessTokenSilently, DELETE, `/jobs/${jobId}/photos/gallery`);

/**
 * Renames a gallery photo - how the gallery is reordered, since the vehicle page shows it in filename
 * order. Refused, not applied, if another photo already has the new name.
 */
export const renameGalleryPhoto = (getAccessTokenSilently, jobId, name, to) =>
    callRemote(getAccessTokenSilently, POST, `/jobs/${jobId}/photos/gallery/${encodeURIComponent(name)}/rename`, {to});

/** "car_001.jpg" -> {stem: "car_001", extension: ".jpg"}. A name with no dot has no extension. */
export const splitName = (name) => {
    const dot = name.lastIndexOf('.');
    return dot > 0 ? {stem: name.slice(0, dot), extension: name.slice(dot)} : {stem: name, extension: ''};
};

/**
 * Checks a new stem for a gallery photo before asking the API. The extension is kept - renaming is for
 * ordering, and a PNG named .jpg would be a lie - so only the stem is the editor's.
 * Returns {to, problem}: the full new name, and a reason it cannot be used or null.
 */
export const planRename = (gallery, name, newStem) => {
    const to = newStem.trim() + splitName(name).extension;
    if (!newStem.trim()) {
        return {to, problem: 'The name cannot be blank'};
    }
    if (to === name) {
        return {to, problem: null};
    }
    const problem = galleryFileProblem({name: to, type: ''});
    if (problem) {
        return {to, problem};
    }
    if (gallery.some(photo => photo.name === to)) {
        return {to, problem: `There is already a photo named "${to}"`};
    }
    return {to, problem: null};
};

// A single image's kind is the last segment of its key.
export const kindOf = (photo) => photo.key.split('/').pop();

/**
 * Where to show a photo from: ECT's public listing bucket (REACT_APP_LISTING_CONTENT_BASE - not
 * REACT_APP_CONTENT_BASE, the older ect-listing-content bucket). A replaced photo keeps its key, so
 * lastModified goes on the URL to stop the browser showing the old one.
 */
export const photoUrl = (photo) => {
    const path = photo.key.split('/').map(encodeURIComponent).join('/');
    const version = photo.lastModified ? `?v=${Date.parse(photo.lastModified)}` : '';
    return `${process.env.REACT_APP_LISTING_CONTENT_BASE}/${path}${version}`;
};

/** What is still needed before the job can be submitted, as labels to show the editor. */
export const missingForSubmit = (photos) => {
    const present = new Set(photos.images.map(kindOf));
    const missing = SINGLE_IMAGES.filter(image => !present.has(image.kind)).map(image => image.label);
    if (photos.gallery.length === 0) {
        missing.push('Gallery photos');
    }
    return missing;
};

/**
 * Checks a batch of gallery files before any of it is uploaded. A file with the name of one already
 * in the gallery replaces it - S3 is keyed by name - so those are reported for the editor to see.
 * Returns {files, replacing, problems}; nothing should be uploaded while there are problems.
 */
export const planGalleryAdd = (gallery, files) => {
    const problems = files.map(galleryFileProblem).filter(Boolean);
    const names = files.map(f => f.name.trim());
    const duplicate = names.find((name, i) => names.indexOf(name) !== i);
    if (duplicate) {
        problems.push(`Two of the photos you chose are both named "${duplicate}"`);
    }
    const existing = new Set(gallery.map(photo => photo.name));
    return {files, replacing: names.filter(name => existing.has(name)), problems};
};

/**
 * Whether the photos in S3 differ from what the listing was last updated with, so a manager knows the
 * live page is behind. A photo newer than the last update is an upload or replacement; a different
 * gallery count is a removal (or an addition). Null when the listing has never been updated.
 */
export const changedSinceListingUpdate = (photos, job) => {
    if (!job.photosCompletedDate) {
        return null;
    }
    const updated = Date.parse(job.photosCompletedDate);
    const newer = [...photos.images, ...photos.gallery]
        .some(photo => photo.lastModified && Date.parse(photo.lastModified) > updated);
    return newer || photos.gallery.length !== job.galleryPhotoCount;
};

/** Asks ECT to show the photos as they are now. An editor manager's; the job's state is untouched. */
export const updateListing = (getAccessTokenSilently, jobId) =>
    callRemote(getAccessTokenSilently, POST, `/jobs/${jobId}/photos/update-listing`, {});

// The transparent cutout needs a format that can hold transparency; a JPEG would fill it with a background.
const TRANSPARENT_TYPES = ['image/png', 'image/webp', 'image/avif'];

/** A reason this file cannot go in this single-image slot, or null. */
export const singleImageProblem = (kind, file) => {
    const problem = galleryFileProblem(file);
    if (problem) {
        return problem;
    }
    if (kind === 'passenger-transparent' && !TRANSPARENT_TYPES.includes(contentTypeOf(file))) {
        return `"${file.name}" is a JPEG, which cannot be transparent - use the PNG cutout`;
    }
    return null;
};

export const errorMessage = (e) => {
    const data = e && e.response && e.response.data;
    if (typeof data === 'string' && data.length > 0 && data.length < 500) {
        return data;
    }
    return (e && e.message) || 'Something went wrong';
};

/**
 * Uploads single images ({kind: File}) and gallery files (File[]) for an editor job - a whole gallery
 * or one more batch of it. onProgress(done, total) is called as files land. Resolves to the S3 keys
 * uploaded; rejects if any file still fails after retrying.
 */
export const uploadEditorPhotos = async (getAccessTokenSilently, jobId, images, gallery, onProgress) => {
    // Every file becomes a pending item, matched back to its grant by kind + name.
    let pending = [
        ...Object.entries(images).filter(([, file]) => file).map(([kind, file]) => ({kind, file})),
        ...gallery.map(file => ({kind: 'gallery', file})),
    ];
    const total = pending.length;
    const uploadedKeys = [];

    for (let attempt = 1; attempt <= ATTEMPTS && pending.length > 0; attempt++) {
        // Fresh URLs every round: they expire after an hour, and a retry only needs the failures.
        const grants = await requestGrants(getAccessTokenSilently, jobId, pending);
        const failed = [];

        await runInParallel(pending, PARALLEL_UPLOADS, async (item) => {
            const grant = grants.find(g => matches(g, item));
            try {
                if (!grant) {
                    throw new Error(`No upload URL came back for ${item.file.name}`);
                }
                await put(grant, item.file);
                uploadedKeys.push(grant.key);
                onProgress(uploadedKeys.length, total);
            } catch (e) {
                failed.push({...item, error: e});
            }
        });
        pending = failed;
    }

    if (pending.length > 0) {
        throw new Error(`${pending.length} of ${total} photos did not upload (${pending.slice(0, 3).map(i => i.file.name).join(', ')}`
            + `${pending.length > 3 ? ', ...' : ''}): ${pending[0].error.message}`);
    }
    return uploadedKeys;
};

const requestGrants = async (getAccessTokenSilently, jobId, items) => {
    // Each single image with its file's real type, which ECT signs: its key has no extension, so the
    // stored type is the only thing saying the transparent cutout is a PNG.
    const images = items.filter(i => i.kind !== 'gallery').map(i => ({kind: i.kind, contentType: contentTypeOf(i.file)}));
    const gallery = items.filter(i => i.kind === 'gallery')
        .map(i => ({name: i.file.name.trim(), contentType: contentTypeOf(i.file)}));

    const grants = [];
    for (let start = 0; start === 0 || start < gallery.length; start += MAX_GALLERY_PER_REQUEST) {
        const response = await callRemote(getAccessTokenSilently, POST, `/jobs/${jobId}/upload-urls`, {
            images: start === 0 ? images : [],
            gallery: gallery.slice(start, start + MAX_GALLERY_PER_REQUEST),
        });
        grants.push(...response.uploads);
    }
    return grants;
};

// Gallery grants carry the filename; single images have name null and are identified by the key's
// last segment, which is the kind that was asked for.
const matches = (grant, item) => item.kind === 'gallery'
    ? grant.kind === 'gallery' && grant.name === item.file.name.trim()
    : grant.kind !== 'gallery' && grant.key.split('/').pop() === item.kind;

const put = async (grant, file) => {
    // Content-Type must be exactly what ECT signed, and nothing else may be added: S3 answers
    // 403 SignatureDoesNotMatch otherwise. Errors come back as S3's XML, not JSON.
    const response = await fetch(grant.url, {
        method: grant.method || 'PUT',
        headers: {'Content-Type': grant.contentType},
        body: file,
    });
    if (!response.ok) {
        const body = await response.text();
        const code = (body.match(/<Code>([^<]+)<\/Code>/) || [])[1];
        throw new Error(`S3 answered ${response.status}${code ? ' ' + code : ''} for ${file.name}`);
    }
};

const runInParallel = async (items, limit, worker) => {
    let next = 0;
    const lanes = Array.from({length: Math.min(limit, items.length)}, async () => {
        while (next < items.length) {
            await worker(items[next++]);
        }
    });
    await Promise.all(lanes);
};
